import { useLocation, useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useProfileDrawer } from '../../context/ProfileDrawerContext';
import ProfileDrawer from './ProfileDrawer';
import NotificationDropdown from './NotificationDropdown';

const HeaderNew = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { isOpen: isProfileOpen, openDrawer, closeDrawer } = useProfileDrawer();

  // Show all nav links only when logged in, otherwise just Home
  const navLinks = isAuthenticated ? [
    { label: 'Home', path: '/home-new', enabled: true },
    { label: 'Book Gold', path: '/book-gold', enabled: true },
    { label: 'Refund Gold', path: '/refund-gold', enabled: true },
    { label: 'Redeem Gold', path: '/redeem-gold', enabled: true },
    { label: 'Transaction History', path: '/transaction-history', enabled: true },
  ] : [
    { label: 'Home', path: '/home-new', enabled: true },
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavClick = (e, link) => {
    e.preventDefault();
    if (link.enabled && link.path !== '#') {
      navigate(link.path);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-[#00302B] to-[#004D40] rounded-lg mx-2 mt-2 shadow-lg">
      <div className="w-full px-4 lg:px-6">
        <div className="flex items-center h-16">
          {/* Logo Section - Left aligned */}
          <div className="flex items-center shrink-0">
            <div className="flex items-center gap-3">
              {/* Golden Diamond Logo */}
              <img 
                src="/assets-new/mothers-gold-logo.png" 
                alt="Mothers Gold Diamond" 
                className="h-10 w-auto object-contain"
              />
              
              {/* Brand Text - 3 lines */}
              <div className="flex flex-col leading-tight">
                <span className="text-base font-bold text-white tracking-tight">MOTHERS&apos;</span>
                <span className="text-xs text-white/90 tracking-wide font-medium">GOLD & DIAMONDS</span>
                <span className="text-[10px] text-white/70 italic">Manufacturers & Wholesalers</span>
              </div>
            </div>
            
            {/* Tagline - Mint a Milli with Logo */}
            <div className="hidden lg:flex items-center gap-3 ml-4 pl-4 border-l border-white/30">
              {/* Milli Logo with Gold Border */}
              <div className="w-10 h-10 rounded-lg border-2 border-[#B8860B] overflow-hidden flex-shrink-0 shadow-md">
                <img 
                  src="/assets-new/milli-logo.png" 
                  alt="Mint a Milli" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-lg font-semibold text-white">Mint a Milli</p>
                <p className="text-xs text-white/80">Every Milli Counts!</p>
              </div>
            </div>
          </div>

          {/* Navigation Links - Starts at ~30% from left */}
          <nav className="hidden md:flex items-center ml-8 lg:ml-12">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.path}
                onClick={(e) => handleNavClick(e, link)}
                className={`relative px-4 py-5 text-base font-semibold transition-colors ${
                  isActive(link.path)
                    ? 'text-[#FFD700]'
                    : link.enabled 
                      ? 'text-white hover:text-[#FFD700]' 
                      : 'text-white/50 cursor-default'
                }`}
              >
                {link.label}
                {/* Golden underline for active tab */}
                {isActive(link.path) && (
                  <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-[#FFD700] rounded-full" />
                )}
              </a>
            ))}
          </nav>

          {/* Spacer to push right side to edge */}
          <div className="flex-1" />

          {/* Right Side - Pushed to very right */}
          <div className="flex items-center gap-4">
            {/* Notification Dropdown */}
            <NotificationDropdown />
            
            {/* Profile Avatar */}
            <button 
              onClick={openDrawer}
              className="focus:outline-none"
            >
              {user?.profile_picture ? (
                <img
                  src={user.profile_picture}
                  alt="Profile"
                  className="w-9 h-9 rounded-full object-cover border-2 border-[#FFD700]/70"
                />
              ) : (
                <div className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center border-2 border-white/50 hover:border-[#FFD700]/70 transition-colors">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Profile Drawer */}
      <ProfileDrawer 
        isOpen={isProfileOpen} 
        onClose={closeDrawer} 
      />
    </header>
  );
};

export default HeaderNew;
